import React from 'react';
import GuidedTourOverlay from './guidedTour/GuidedTourOverlay';
import type { GuidedTourStep } from './guidedTour/types';
import { useGuidedTour } from '../hooks/useGuidedTour';
import type { AdminTabType } from './admin/adminTabConfig';

export type AdminOnboardingTab = AdminTabType | 'usage-history';

interface AdminOnboardingProps {
  isOpen: boolean;
  onClose: () => void;
  activeTab: AdminOnboardingTab;
}

const usersSteps: GuidedTourStep[] = [
  {
    id: 'admin-tabs',
    title: 'Admin sections',
    description:
      'Use these tabs to switch between Users, Usage, Billing (per-request history), and Connector admin tools. The Admin Panel is restricted to administrator accounts.',
    position: 'bottom',
    offset: { x: 0, y: 20 },
  },
  {
    id: 'admin-users-section',
    title: 'Users',
    description:
      'This tab lists all accounts. Scroll the table to load more rows automatically when more data exists.',
    position: 'left',
    offset: { x: -30, y: 0 },
  },
  {
    id: 'admin-users-section-header',
    title: 'Users header',
    description:
      'The section title is on the left; use Help next to it for this tour. Search filters the list; Search and Refresh reload data from the server.',
    position: 'left',
    offset: { x: -30, y: 0 },
  },
  {
    id: 'admin-users-table-wrap',
    title: 'User table',
    description:
      'Each row shows identity, role, token balance, and actions. Use Edit for roles (not your own) and Add tokens to top up balances.',
    position: 'top',
    offset: { x: 0, y: -20 },
  },
];

const usageSteps: GuidedTourStep[] = [
  {
    id: 'admin-tabs',
    title: 'Usage tab',
    description:
      'Per-user token aggregates: prompt, completion, totals, and estimated total cost. Use this tab for a high-level view before drilling into Billing for line items.',
    position: 'bottom',
    offset: { x: 0, y: 20 },
  },
  {
    id: 'admin-usage-section-header',
    title: 'Token usage',
    description:
      'The section title is on the left; Help opens this tour. The table below lists users with activity when data is available.',
    position: 'left',
    offset: { x: -30, y: 0 },
  },
  {
    id: 'admin-usage-table-wrap',
    title: 'Usage table',
    description:
      'Compare prompt vs completion tokens and total cost in USD (6 decimal places). Scroll down to load more rows when applicable.',
    position: 'top',
    offset: { x: 0, y: -20 },
  },
];

const billingSteps: GuidedTourStep[] = [
  {
    id: 'admin-tabs',
    title: 'Billing / history',
    description:
      'This tab shows chronological usage history across users: filters, totals, and per-record detail when you open a row.',
    position: 'bottom',
    offset: { x: 0, y: 20 },
  },
  {
    id: 'admin-billing-section-header',
    title: 'Usage history',
    description:
      'Section title on the left; Help opens this tour. Refresh reloads the current page. Summary totals appear below when data is loaded.',
    position: 'left',
    offset: { x: -30, y: 0 },
  },
  {
    id: 'admin-billing-filters',
    title: 'Filters',
    description:
      'Restrict by functionality type, user ID, and date range. Apply filters runs the query; Clear removes applied filters. Invalid ranges show an inline error.',
    position: 'left',
    offset: { x: -30, y: 0 },
  },
  {
    id: 'admin-billing-table-wrap',
    title: 'History table',
    description:
      'Each record links to a detail view when supported. Pagination appears above the table when there are more pages.',
    position: 'top',
    offset: { x: 0, y: -20 },
    scrollIntoViewBlock: 'nearest',
  },
];

const nltSteps: GuidedTourStep[] = [
  {
    id: 'admin-tabs',
    title: 'NLT Configs tab',
    description:
      'Global NLT templates define the default multi-agent setup used by the Natural Language Tuning assistant. Switch tabs here to manage users, usage, billing, connector integrations, and fine-tuning lineage.',
    position: 'bottom',
    offset: { x: 0, y: 20 },
  },
  {
    id: 'admin-nlt-config-section',
    title: 'NLT configuration workspace',
    description:
      'This section lists saved templates and hosts the configurator below. Templates are versioned; the latest active config is highlighted at the top when available.',
    position: 'left',
    offset: { x: -30, y: 0 },
  },
  {
    id: 'admin-nlt-config-header',
    title: 'Search and actions',
    description:
      'Search configs by name, refresh the list, open Help to replay this tour, or start a new template with New Config.',
    position: 'left',
    offset: { x: -30, y: 0 },
  },
  {
    id: 'admin-nlt-config-list',
    title: 'Saved templates',
    description:
      'Each row shows name, ID, and last update. Edit loads a template into the configurator; Delete removes it permanently. When no configs exist yet, create one below.',
    position: 'top',
    offset: { x: 0, y: -20 },
    scrollIntoViewBlock: 'nearest',
  },
  {
    id: 'admin-nlt-config-editor',
    title: 'Template configurator',
    description:
      'Create or edit the coordinator and worker agents, tools, and prompts for NLT. Save stores a new version; Cancel (when editing) returns to a blank new template.',
    position: 'top',
    offset: { x: 0, y: -20 },
    scrollIntoViewBlock: 'nearest',
  },
];

const connectorSteps: GuidedTourStep[] = [
  {
    id: 'admin-tabs',
    title: 'Connector (admin)',
    description:
      'Manage integrations from an admin perspective: list all integrations, select one, edit URL/API key, assign users, and edit the stored multi-agent configuration.',
    position: 'bottom',
    offset: { x: 0, y: 20 },
  },
  {
    id: 'admin-connector-section-header',
    title: 'Integrations header',
    description:
      'Section title on the left; Help opens this tour. Refresh reloads the list; Assign User opens a modal for the selected integration.',
    position: 'left',
    offset: { x: -30, y: 0 },
  },
  {
    id: 'admin-connector-table-wrap',
    title: 'Select an integration',
    description:
      'Use Select to choose which integration_id the Virtual User service and configurator use. Edit, Copy ID, and Delete are available from the actions column.',
    position: 'top',
    offset: { x: 0, y: -20 },
  },
  {
    id: 'admin-connector-configurator',
    title: 'Integration configurator',
    description:
      'After selecting a row, load and edit the JSON-backed agent configuration here and save changes back to the integration record.',
    position: 'top',
    offset: { x: 0, y: -20 },
    scrollIntoViewBlock: 'nearest',
  },
];

function getStepsForTab(tab: AdminOnboardingTab): GuidedTourStep[] {
  switch (tab) {
    case 'users':
      return usersSteps;
    case 'usage':
      return usageSteps;
    case 'billing':
    case 'usage-history':
      return billingSteps;
    case 'connector':
      return connectorSteps;
    case 'nlt':
      return nltSteps;
    default:
      return usersSteps;
  }
}

const AdminOnboarding: React.FC<AdminOnboardingProps> = ({ isOpen, onClose, activeTab }) => {
  const steps = getStepsForTab(activeTab);
  const tourTitles: Partial<Record<AdminOnboardingTab, string>> = {
    users: 'Admin — Users',
    usage: 'Admin — Usage',
    billing: 'Admin — Billing',
    'usage-history': 'Admin — Billing',
    connector: 'Admin — Connector',
    nlt: 'Admin — NLT Configs',
  };

  const tour = useGuidedTour({
    isOpen,
    steps,
    onClose,
    layout: 'anchor',
    resetKey: activeTab,
    extraHighlightClasses: ['profile-onboarding-spotlight'],
  });

  if (!isOpen || !tour.currentStepData) return null;

  return (
    <GuidedTourOverlay
      isOpen={isOpen}
      tourTitle={tourTitles[activeTab] ?? 'Admin guided tour'}
      stepsCount={tour.stepCount}
      currentStep={tour.safeStepIndex}
      stepTitle={tour.currentStepData.title}
      stepDescription={tour.currentStepData.description}
      contextLabel={tour.currentStepData.contextLabel}
      tooltipX={tour.tooltipPosition.x}
      tooltipY={tour.tooltipPosition.y}
      position={tour.currentPosition}
      isStepTransitioning={tour.isStepTransitioning}
      onClose={tour.handleSkip}
      onNext={tour.handleNext}
      onPrev={tour.handlePrev}
    />
  );
};

export default AdminOnboarding;
